import { Download, FileText, Trash } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import {
  CaseDocument,
  Document as ApiDocument,
  DocumentEntityType,
  DocumentType,
} from "../../../../src/api/caseDocuments";
import { LEAType, LEATypeLabelMap } from "../../../../src/api/events";
import { useAppDispatch, useAppSelector } from "../../../../src/app/store";
import { fetchDocumentDownloadUrl } from "../../../../src/thunks/caseDocument";
import { DOCUMENT_CONFIG } from "../config/DocumentConfig";
import DeleteDocumentModal from "./DeleteDocumentModal";
import DocumentTooltip from "./DocumentTooltip";
import { getAllPartyPerpetrators } from "../../stakeholder/logic/stakeholderSlice";
import { getAccountProductInfoByAccountNumber } from "../../creditAccounts/logic/slice";

export interface DocumentRowProps {
  document: CaseDocument;
  onDelete: (document: CaseDocument) => void;
  onDownload: (document: CaseDocument) => void;
  onClick: (document: CaseDocument) => void;
  hideDocumentTypePill?: boolean;
}

function DocumentRow({
  document,
  onDelete,
  onDownload,
  onClick,
  hideDocumentTypePill = false,
}: DocumentRowProps) {
  const { file_name, created_at, document_type, partyName, notes, loanAccountId, leaName } = document;
  const displayLoanAccountNumber = useAppSelector((state) =>
    loanAccountId
      ? getAccountProductInfoByAccountNumber(state, loanAccountId)!.ext_account_number
      : undefined,
  );

  const config = DOCUMENT_CONFIG[document_type];

  const documentInfoText = `${new Date(created_at).toLocaleDateString("en-US", {month: "short", day: "2-digit", year: "numeric",})}
  ${partyName ? ` • Party Name: ${partyName}` : ""}
  ${displayLoanAccountNumber ? `• Loan ID: ${displayLoanAccountNumber}` : ""}
  ${leaName ? ` • LEA: ${leaName}` : ""}
  `;

  return (
    <div className="flex flex-col py-2 rounded-mdhover:bg-gray-50 cursor-pointer"
      >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="bg-slate-50 rounded-md p-2">
            <FileText className="h-4 w-4 text-slate-600 flex-shrink-0" />
          </div>

          <div className="flex flex-col min-w-0">
            <DocumentTooltip content={file_name}>
              <span
                className="font-medium text-black text-sm truncate"
                onClick={() => {onClick(document)}}
              >
                {file_name}
              </span>
            </DocumentTooltip>

            <DocumentTooltip content={documentInfoText}>
              <span className="text-xs text-gray-500 truncate font-normal leading-4">
                {documentInfoText}
              </span>
            </DocumentTooltip>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {!hideDocumentTypePill && (
            <div
              className="
                rounded-[8px]
                border border-transparent
                bg-gray-100
                flex
                min-w-5
                px-2 py-0.5
                justify-center
                items-center
                gap-1
              "
            >
              <span className="text-gray-600 text-xs font-semibold leading-4">
                {DOCUMENT_CONFIG[document_type]?.displayName ?? document_type}
              </span>
            </div>
          )}

          <button
            className="p-0.5 rounded hover:bg-gray-200"
            onClick={(e) => {
              e.stopPropagation();
              onDownload(document);
            }}
          >
            <Download className="h-4 w-4 text-[#737373]" />
          </button>
            <button
              className="p-0.5 rounded hover:bg-gray-200"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(document);
              }}
            >
              <Trash className="h-4 w-4 text-[#737373]" />
            </button>
        </div>
      </div>

      {config?.requiresNote && notes && (
        <DocumentTooltip content={notes}>
          <span className="mt-2 pl-10 text-xs leading-4 font-normal text-gray-500 line-clamp-1 cursor-pointer">
            {notes}
          </span>
        </DocumentTooltip>
      )}
    </div>
  );
}

export interface DocumentProps {
  documents: ApiDocument[];
  hideDocumentTypePill?: boolean;
}

export default function Documents({ documents, hideDocumentTypePill = false }: DocumentProps) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] =
    useState<CaseDocument | null>(null);

  const dispatch = useAppDispatch();

  const parties = useAppSelector(getAllPartyPerpetrators);

  const documentsWithDerivedFields = useMemo(() => {
    const partyMap = new Map(parties.map((p) => [p.party_id, p.name]));

    return documents.map((doc) => ({
      ...doc,
      partyName:
        doc.entity_type === DocumentEntityType.PARTY_INVOLVED
          ? partyMap.get(doc.entity_id) ?? null
          : null,
      loanAccountId:
        doc.entity_type === DocumentEntityType.LOAN_ACCOUNT
          ? doc.entity_id
          : null,
      leaName:
        doc.document_type === DocumentType.LEA_REPORT
          ? LEATypeLabelMap[doc.entity_id as LEAType] ?? doc.entity_id
          : null,
    }));
  }, [documents, parties]);

  const handleDelete = useCallback((document: CaseDocument) => {
    setSelectedDocument(document);
    setDeleteOpen(true);
  }, []);

  const handleCloseDelete = useCallback(() => {
    setDeleteOpen(false);
    setSelectedDocument(null);
  }, []);

  const handleDownload = useCallback(
    async (document: CaseDocument) => {
      try {
        const { download_url } = await dispatch(
          fetchDocumentDownloadUrl({
            document_id: document.document_id,
            params: {
              file_reference: document.file_reference,
              mode: "DOWNLOAD",
            },
          })
        ).unwrap();

        window.open(download_url, "_self");
      } catch (error) {
        console.error("Failed to download document:", error);
        alert("Unable to download the document. Please try again.");
      }
    },
    [dispatch]
  );

  const handleClick = useCallback(
    async (document: CaseDocument) => {
      try {
        const { download_url } = await dispatch(
          fetchDocumentDownloadUrl({
            document_id: document.document_id,
            params: {
              file_reference: document.file_reference,
              mode:"VIEW",
            },
          })
        ).unwrap();

        window.open(download_url, "_blank", "noopener,noreferrer");
      } catch (error) {
        console.error("Failed to open document:", error);
      }
    },
    [dispatch]
  );

  return (
    <>
      {documentsWithDerivedFields?.map(
        (document: CaseDocument, index: number) => (
          <div key={document.document_id}>
            <DocumentRow
              document={document}
              onDelete={handleDelete}
              onDownload={handleDownload}
              onClick={handleClick}
              hideDocumentTypePill={hideDocumentTypePill}
            />

            {index < documentsWithDerivedFields.length - 1 && (
              <div className="py-3">
                <div className="h-px bg-[#E5E5E5] w-full" />
              </div>
            )}
          </div>
        )
      )}

      {deleteOpen && selectedDocument && (
        <DeleteDocumentModal
          onClose={handleCloseDelete}
          fileName={selectedDocument?.file_name || "document"}
          selectedDocumentId={selectedDocument.document_id}
        />
      )}
    </>
  );
}
